import { toast } from "sonner";
import { ArrowRight, Copy, EnvelopeSimple, XCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import InvitationHistoryPanel from "./InvitationHistoryPanel";

// Human-readable "time left" for a pending invite. Returns a color-coded
// pill config so the row can highlight invites getting close to expiry.
function invitationTimeLeft(inv) {
  if (!inv.expires_at) return null;
  const now = new Date();
  const expires = new Date(inv.expires_at);
  const ms = expires - now;
  if (ms <= 0) return { text: "EXPIRED", color: "bg-red-50 text-[#D0021B] border-red-200" };
  const hours = ms / 3_600_000;
  const label = hours >= 24
    ? `${Math.floor(hours / 24)}d ${Math.floor(hours % 24)}h`
    : hours >= 1 ? `${Math.floor(hours)}h ${Math.floor((hours % 1) * 60)}m`
    : `${Math.max(1, Math.round(ms / 60_000))}m`;
  const color = hours < 6
    ? "bg-red-50 text-[#D0021B] border-red-200"
    : hours < 12
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-zinc-50 text-zinc-700 border-zinc-200";
  return { text: `${label} LEFT`, color };
}

function UsersTable({ users, onChanged }) {
  const { user: currentUser } = useAuth();
  const endDate = async (u) => {
    if (!window.confirm(`End-date user "${u.email}"?\n\nThey will be blocked from signing in immediately. You can reactivate them later. Audit history is preserved.`)) return;
    try {
      await api.patch(`/tenant/users/${u.id}/deactivate`, {});
      toast.success(`${u.email} has been end-dated`);
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to end-date user");
    }
  };
  const reactivate = async (u) => {
    try {
      await api.patch(`/tenant/users/${u.id}/reactivate`, {});
      toast.success(`${u.email} reactivated`);
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to reactivate user");
    }
  };
  const canManage = currentUser?.role === "admin" || currentUser?.role === "superuser";
  return (
    <table className="w-full text-sm border border-zinc-200">
      <thead className="bg-zinc-50 border-b border-zinc-200">
        <tr className="text-xs tracking-wider uppercase">
          <th className="text-left px-4 py-2.5">Name</th>
          <th className="text-left px-4 py-2.5">Email</th>
          <th className="text-left px-4 py-2.5">Role</th>
          <th className="text-left px-4 py-2.5">Auth</th>
          <th className="text-left px-4 py-2.5">MFA</th>
          <th className="text-left px-4 py-2.5">Status</th>
          {canManage && <th className="text-right px-4 py-2.5">Action</th>}
        </tr>
      </thead>
      <tbody data-testid="users-tbody">
        {users.map((u) => {
          const isEnded = u.active === false || Boolean(u.end_date);
          const isSelf = currentUser?.email === u.email;
          return (
            <tr key={u.id} className={`border-b border-zinc-100 ${isEnded ? "bg-red-50/40" : ""}`}>
              <td className="px-4 py-2.5 font-medium">{u.name}</td>
              <td className="px-4 py-2.5 font-mono text-xs">{u.email}</td>
              <td className="px-4 py-2.5"><span className="text-[10px] font-mono px-1.5 py-0.5 border border-zinc-300 uppercase">{u.role}</span></td>
              <td className="px-4 py-2.5 text-xs text-zinc-500 capitalize">{u.auth_provider || "password"}</td>
              <td className="px-4 py-2.5">{u.mfa_enabled ? <span className="text-[10px] text-emerald-700 font-mono">ON</span> : <span className="text-[10px] text-zinc-400 font-mono">OFF</span>}</td>
              <td className="px-4 py-2.5">
                {isEnded ? (
                  <span className="text-[10px] px-1.5 py-0.5 bg-[#D0021B] text-white font-mono">END-DATED{u.end_date ? ` · ${u.end_date}` : ""}</span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 font-mono">ACTIVE</span>
                )}
              </td>
              {canManage && (
                <td className="px-4 py-2.5 text-right">
                  {u.role === "superuser" || isSelf ? (
                    <span className="text-[9px] font-mono text-zinc-400">{isSelf ? "SELF" : "PROTECTED"}</span>
                  ) : isEnded ? (
                    <button
                      onClick={() => reactivate(u)}
                      data-testid={`reactivate-user-${u.id}`}
                      className="text-[10px] font-mono tracking-wider uppercase text-emerald-700 border border-emerald-600 px-2 py-0.5 hover:bg-emerald-600 hover:text-white transition-colors"
                    >
                      Reactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => endDate(u)}
                      data-testid={`end-date-user-${u.id}`}
                      className="text-[10px] font-mono tracking-wider uppercase text-[#D0021B] border border-[#D0021B] px-2 py-0.5 hover:bg-[#D0021B] hover:text-white transition-colors"
                    >
                      End-date
                    </button>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function InvitationsTable({ invitations, isAdmin, onReload }) {
  if (!invitations.length) return null;
  const pendingCount = invitations.filter((i) => !i.used).length;

  const rescind = async (inv) => {
    if (!window.confirm(
      `Rescind the pending invitation to ${inv.email}?\n\n` +
      `An apology email will be sent to them and the invitation will be permanently removed. ` +
      `You can invite the same address again immediately after.`
    )) return;
    try {
      await api.delete(`/tenant/invitations/${inv.id}`);
      toast.success(`Invitation to ${inv.email} rescinded`);
      onReload && onReload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to rescind invitation");
    }
  };

  return (
    <>
      <div className="label-sm text-zinc-500 mt-6 mb-2 flex items-baseline gap-2">
        <span>Pending Invitations ({pendingCount})</span>
        <span className="font-mono text-[10px] normal-case text-zinc-400">· 48h window · auto-expires with an email notice</span>
      </div>
      <table className="w-full text-sm border border-zinc-200">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">Email</th>
            <th className="text-left px-4 py-2.5">Role</th>
            <th className="text-left px-4 py-2.5">Status · Time Left</th>
            <th className="text-left px-4 py-2.5">Invited By</th>
            <th className="w-40" />
          </tr>
        </thead>
        <tbody>
          {invitations.map((i) => {
            const timeLeft = !i.used ? invitationTimeLeft(i) : null;
            return (
              <tr key={i.id} className="border-b border-zinc-100" data-testid={`invitation-row-${i.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs">{i.email}</td>
                <td className="px-4 py-2.5 text-xs uppercase">{i.role}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] px-1.5 py-0.5 mr-1 ${i.used ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {i.used ? "ACCEPTED" : "PENDING"}
                  </span>
                  {timeLeft && (
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.5 border ${timeLeft.color}`}
                      data-testid={`invitation-timeleft-${i.id}`}
                      title={i.expires_at ? `Expires ${new Date(i.expires_at).toLocaleString()}` : ""}
                    >
                      {timeLeft.text}
                    </span>
                  )}
                  {!i.used && i.nudged_at && (
                    <span
                      className="text-[10px] font-mono px-1.5 py-0.5 mr-1 border border-emerald-300 bg-emerald-50 text-emerald-700"
                      data-testid={`invitation-nudged-${i.id}`}
                      title={`24h reminder sent ${new Date(i.nudged_at).toLocaleString()}`}
                    >
                      ✓ REMINDER SENT
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-xs text-zinc-500">{i.invited_by}</td>
                <td className="px-4 py-2.5">
                  {!i.used && (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/register?invite=${i.token}`); toast.success("Link copied"); }}
                        className="text-[10px] font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1"
                        data-testid={`invitation-copy-${i.id}`}
                      >
                        <Copy size={10} /> COPY
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => rescind(i)}
                          data-testid={`invitation-rescind-${i.id}`}
                          className="text-[10px] font-mono text-[#D0021B] hover:text-red-800 flex items-center gap-1"
                          title="Rescind invitation — sends an apology email and removes the pending record."
                        >
                          <XCircle size={10} /> RESCIND
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

export default function TeamSection({ users, invitations, inviteEmail, setInviteEmail, inviteRole, setInviteRole, onSendInvite, onReload }) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superuser";
  return (
    <section className="border border-zinc-950 mt-6">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase">team.invite</div>
      <div className="p-6">
        <form onSubmit={onSendInvite} className="flex flex-wrap items-end gap-3 mb-6" data-testid="invite-form">
          <div className="flex-1 min-w-[200px]">
            <label className="label-sm text-zinc-500">Invite email</label>
            <div className="flex items-center gap-3 border-b border-zinc-300 focus-within:border-zinc-950 py-2">
              <EnvelopeSimple size={14} className="text-zinc-400" />
              <input
                data-testid="invite-email-input"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                placeholder="colleague@company.com"
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>
          </div>
          <div>
            <label className="label-sm text-zinc-500">Role</label>
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="block border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none">
              {["user", "admin", "viewer", "auditor"].map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2.5 hover:bg-[#FF3E00] flex items-center gap-2" data-testid="invite-send-btn">
            SEND INVITE <ArrowRight size={12} weight="bold" />
          </button>
        </form>

        <div className="label-sm text-zinc-500 mb-2">Team Members ({users.length})</div>
        <UsersTable users={users} onChanged={onReload} />
        <InvitationsTable invitations={invitations} isAdmin={isAdmin} onReload={onReload} />
        {isAdmin && <InvitationHistoryPanel />}
      </div>
    </section>
  );
}
